import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect, useRef } from "react";
import { ApproveAndPayModal } from "@/components/approve-and-pay-modal";
import { ApproveTaskDialog } from "@/components/approve-task-dialog";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { Link, useSearch } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Star, CheckCircle, CreditCard, DollarSign, TrendingUp, Clock, Download, MessageCircle, X, Info, AlertTriangle, Paperclip, Loader2, Archive } from "lucide-react";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import { AttachmentPreviews } from "@/components/message-attachments";
import type { OperationsTask, Vendor, Property, PaymentTransaction, Owner, OwnerVendorApproval, PropertyGroup } from "@shared/schema";
import { computeVendorFeeBreakdown, netVendorPayoutAmount } from "@shared/fees";
import { MANUAL_PAYMENT_METHODS } from "@shared/schema";
import { resolvePaymentNotice } from "@/lib/payment-notice";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { StatusBadge } from "@/pages/app-financials/CardsTab";
import { uploadMimeType } from "@/lib/upload-mime";

// Task #3939 — Minimal Mercury IO Credit Card shape used by the approval modal
// to show the card assigned to the task's property (last-4 + live status). Full
// shape lives in the Financials → Cards hub (`EnrichedCard`).
type MercuryCardLite = {
  id: number;
  nickname: string | null;
  last4: string | null;
  mercuryCardStatus: string | null;
  assignedPropertyId: number | null;
};

// Task #4074 — Human-readable labels for the off-platform payment methods.
const MANUAL_METHOD_LABELS: Record<string, string> = {
  card: "Card",
  cash: "Cash",
  check: "Check",
  zelle: "Zelle",
  venmo: "Venmo",
};

// Task #4074 — A recorded manual (off-platform) vendor payment, enriched by the
// GET /api/app/operations/manual-payments route with task + vendor display info.
type ManualPaymentRow = {
  id: number;
  taskId: number;
  amountCents: number;
  method: string;
  cardId: number | null;
  note: string | null;
  proofUrls: string[] | null;
  recordedByUserId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  taskTitle: string | null;
  propertyId: number | null;
  vendorId: number | null;
  vendorName: string | null;
};

// Server-decorated payment transaction with the new fee-model fields.
type PaymentTransactionWithBreakdown = PaymentTransaction & {
  padpulseFeeCents?: number | null;
  vendorPayoutCents?: number | null;
};

// Task #2517 — Definitive payment-recording status for a single approved task,
// returned by GET /api/app/operations/approvals/:id/payment-status. Lets the
// Payment History notice distinguish "still processing" from "failed to record"
// instead of guessing from a client-side timeout.
type PaymentRecordingStatus = {
  taskId: number;
  taskStatus: string;
  chargeExpected: boolean;
  outcome: "recorded" | "no_charge_needed" | "failed_to_record" | "processing";
  reference: string;
  payment: PaymentTransactionWithBreakdown | null;
};

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none"
          data-testid={`star-${star}`}
        >
          <Star
            className={`h-6 w-6 transition-colors ${
              star <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-200 hover:text-yellow-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Task #4085 — Shared proof-of-payment uploader for the Add/Edit manual payment
// forms: a file picker (image/PDF, multiple) plus a removable list of staged
// attachments. Upload itself is owned by the parent via `onSelect`.
function ManualProofField({
  urls,
  uploading,
  readOnly,
  onSelect,
  onRemove,
  idPrefix,
}: {
  urls: string[];
  uploading: boolean;
  readOnly: boolean;
  onSelect: (files: FileList | null) => void;
  onRemove: (idx: number) => void;
  idPrefix: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">
        Proof of payment (optional)
      </label>
      <p className="text-xs text-gray-500 mb-2">
        Attach a receipt, check image, or Zelle/Venmo screenshot (image or PDF).
      </p>
      <div className="flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border cursor-pointer ${
            readOnly || uploading ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
          }`}
          data-testid={`label-${idPrefix}-proof-upload`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Add files"}
          <input
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            disabled={readOnly || uploading}
            onChange={(e) => {
              onSelect(e.target.files);
              e.target.value = "";
            }}
            data-testid={`input-${idPrefix}-proof`}
          />
        </label>
      </div>
      {urls.length > 0 && (
        <div className="mt-2" data-testid={`list-${idPrefix}-proof`}>
          <AttachmentPreviews
            urls={urls}
            idPrefix={`${idPrefix}-proof`}
            onRemove={readOnly ? undefined : onRemove}
          />
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-gray-100 text-gray-800",
  partially_refunded: "bg-orange-100 text-orange-800",
  requires_3ds: "bg-purple-100 text-purple-800",
  awaiting_mercury_approval: "bg-indigo-100 text-indigo-800",
  cancelled: "bg-gray-100 text-gray-500",
};

function formatCentsShared(cents: number | null | undefined) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

// Task #1922 — The bolted-on per-row chat dialog was removed. Hosts now jump
// to the unified task detail at `/app/tasks?task=N` which already exposes a
// "Vendor Chat" tab (`VendorChatPanel` in `maintenance-task-detail.tsx`).

// Task #2194 — Render the vendor's line-item price breakdown so the host can
// see *why* the priceCents they're being asked to approve is what it is.
// Source of truth is `task.priceBreakdown` (jsonb, populated by the vendor
// completion handler in server/routes/vendor-portal.ts). Falls back to the
// locked rate fields when priceBreakdown is absent (legacy completions), and
// to nothing when neither is present (the totals row still renders priceCents).
function PriceBreakdownLines({
  task,
  testId,
}: {
  task: OperationsTask;
  testId: string;
}) {
  const breakdown = (task as any).priceBreakdown as
    | {
        rateType?: "hourly" | "flat";
        rateCents?: number;
        hours?: number;
        laborCents?: number;
        flatCents?: number;
        travelFeeCents?: number;
      }
    | null
    | undefined;
  const lockedRateType = (task as any).lockedRateType as "hourly" | "flat" | null | undefined;
  const lockedRateCents = ((task as any).lockedRateCents as number | null | undefined) ?? null;
  const taskTravelFeeCents = ((task as any).travelFeeCents as number | null | undefined) ?? 0;

  const rateType = breakdown?.rateType ?? lockedRateType ?? null;
  if (!rateType) return null;

  const travelFeeCents = breakdown?.travelFeeCents ?? taskTravelFeeCents ?? 0;
  const totalCents = task.priceCents ?? 0;

  return (
    <div
      className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5"
      data-testid={testId}
    >
      {rateType === "hourly" ? (
        <>
          <span data-testid={`${testId}-rate-type`}>Rate type: Hourly</span>
          <span data-testid={`${testId}-rate`}>
            Rate: ${(((breakdown?.rateCents ?? lockedRateCents) || 0) / 100).toFixed(2)}/hr
          </span>
          {breakdown?.hours != null && (
            <span data-testid={`${testId}-hours`}>Hours worked: {breakdown.hours}</span>
          )}
          {breakdown?.laborCents != null && (
            <span data-testid={`${testId}-labor`}>Labor: {formatCentsShared(breakdown.laborCents)}</span>
          )}
        </>
      ) : (
        <>
          <span data-testid={`${testId}-rate-type`}>Rate type: Flat</span>
          <span data-testid={`${testId}-flat`}>
            Flat rate: {formatCentsShared(breakdown?.flatCents ?? lockedRateCents ?? 0)}
          </span>
        </>
      )}
      {travelFeeCents > 0 && (
        <span data-testid={`${testId}-travel`}>Travel fee: {formatCentsShared(travelFeeCents)}</span>
      )}
      <span className="font-medium text-gray-700" data-testid={`${testId}-final`}>
        Final amount: {formatCentsShared(totalCents)}
      </span>
    </div>
  );
}

export default function AppOperationsApprovals() {
  const queryClient = useQueryClient();
  usePageTitle("Vendor Payments");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "approvals", {
    tabs: ["approvals", "history"],
  });
  // Task #2469 — Deep-link from a payment-pending task card on the board.
  // `?task=N` scrolls the matching pending-approval card into view and briefly
  // highlights it so the host knows exactly which task they came to act on.
  const search = useSearch();
  const highlightTaskId = (() => {
    const raw = new URLSearchParams(search).get("task");
    return raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null;
  })();
  const [activeHighlight, setActiveHighlight] = useState<number | null>(null);
  const approvalCardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Task #2475 — Deep-link from an approved (payment-processing) task card lands
  // on the Payment History tab and highlights the matching transaction row so
  // hosts don't have to scan a long table.
  const [activeTxHighlight, setActiveTxHighlight] = useState<number | null>(null);
  const txRowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  // Task #2482 — When a host deep-links from "View Payment Status" but the
  // payment transaction doesn't exist yet (charge still being created), show a
  // small dismissible hint instead of silently opening the History tab.
  const [showTxPendingNotice, setShowTxPendingNotice] = useState(false);
  const [txNoticeDismissed, setTxNoticeDismissed] = useState(false);
  // Task #2509 — Cap how long we wait for a never-arriving charge. Once the
  // polling window elapses we stop refetching and swap the hint for a
  // "taking longer than usual" message instead of polling forever.
  const [txPollTimedOut, setTxPollTimedOut] = useState(false);
  // Task #2509 — A fresh deep-link starts a new wait, so clear any prior
  // timed-out state when the highlighted task changes.
  useEffect(() => {
    setTxPollTimedOut(false);
  }, [highlightTaskId]);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [approveModalTask, setApproveModalTask] = useState<OperationsTask | null>(null);
  // Task #4284 — Archive & Close ($0, no host charge) opens the same popup in
  // archive mode; bulk close runs the dedicated bulk-close endpoint.
  const [archiveModalTask, setArchiveModalTask] = useState<OperationsTask | null>(null);
  // Task #2196 — Reason-required reject dialog
  const [rejectModalTask, setRejectModalTask] = useState<OperationsTask | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  // Edit-an-existing manual payment (Payment History tab).
  const [editingManualPayment, setEditingManualPayment] = useState<ManualPaymentRow | null>(null);
  const [editManualAmount, setEditManualAmount] = useState("");
  const [editManualMethod, setEditManualMethod] = useState("");
  const [editManualNote, setEditManualNote] = useState("");
  const [editManualProofUrls, setEditManualProofUrls] = useState<string[]>([]);
  const [editManualProofUploading, setEditManualProofUploading] = useState(false);
  // Task #4084 — Void (reverse) an existing manual payment.
  const [voidingManualPayment, setVoidingManualPayment] = useState<ManualPaymentRow | null>(null);

  const [statusFilter, setStatusFilter] = useListState("approvals:status", "");
  const [vendorFilter, setVendorFilter] = useListState("approvals:vendor", "");

  const approvalsQuery = useQuery<(OperationsTask & { assignedTeamMemberName?: string | null })[]>({
    queryKey: ["/api/app/operations/approvals"],
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  // Task #3826 — Owner pre-approval wiring. To decide whether a task needs
  // explicit owner sign-off we need (a) the owners with their pre-approved
  // thresholds and the property groups they own, and (b) any approval requests
  // already raised for these tasks. The owners endpoint is PMPro + owner-portal
  // beta gated; for hosts without access it simply 403s and `data` stays
  // undefined, so the "Request owner approval" affordance never appears.
  type OwnersResponse = {
    owners: Owner[];
    groupsByOwner: Record<number, PropertyGroup[]>;
  };
  const ownersQuery = useQuery<OwnersResponse>({
    queryKey: ["/api/app/operations/owners"],
    retry: false,
  });
  const ownerApprovalsQuery = useQuery<OwnerVendorApproval[]>({
    queryKey: ["/api/app/owner-vendor-approvals"],
    retry: false,
  });

  // Properties link to a group by the TEXT match
  // `properties.propertyGroup === propertyGroups.name`; a group links to an
  // owner via `propertyGroups.ownerId`. Mirror that here to resolve a task's
  // owner from its property's group name.
  const ownersById = useMemo(
    () => new Map((ownersQuery.data?.owners || []).map((o) => [o.id, o])),
    [ownersQuery.data],
  );
  const ownerByGroupName = useMemo(() => {
    const map = new Map<string, Owner>();
    const groupsByOwner = ownersQuery.data?.groupsByOwner || {};
    for (const [ownerIdStr, groups] of Object.entries(groupsByOwner)) {
      const owner = ownersById.get(Number(ownerIdStr));
      if (!owner) continue;
      for (const g of groups) {
        if (g?.name) map.set(g.name, owner);
      }
    }
    return map;
  }, [ownersQuery.data, ownersById]);
  const approvalByTaskId = useMemo(
    () => new Map((ownerApprovalsQuery.data || []).map((a) => [a.taskId, a])),
    [ownerApprovalsQuery.data],
  );

  const requestApprovalMutation = useMutation({
    mutationFn: async ({
      taskId,
      ownerId,
      taskTitle,
      taskCostCents,
      propertyAddress,
      vendorName,
    }: {
      taskId: number;
      ownerId: number;
      taskTitle: string;
      taskCostCents: number;
      propertyAddress?: string | null;
      vendorName?: string | null;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/app/owner-portal/task-approvals/${taskId}/request`,
        { ownerId, taskTitle, taskCostCents, propertyAddress, vendorName },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/owner-vendor-approvals"] });
      toast({
        title: "Owner approval requested",
        description: "The owner has been notified and can approve or reject from their portal.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't request owner approval",
        description: parseErrorMessage(e, "Please try again."),
        variant: "destructive",
      });
      // A 409 means an approval already exists — refresh so the row reflects it.
      queryClient.invalidateQueries({ queryKey: ["/api/app/owner-vendor-approvals"] });
    },
  });

  // Task #2503 — While the "payment still being recorded" notice is visible we
  // poll the payment-transactions query every few seconds so the charge appears
  // (and the notice clears + row highlights) without a manual reload. Polling is
  // gated on the notice being visible and not dismissed, so it stops the moment
  // the transaction shows up or the host dismisses the hint.
  // `refetchIntervalInBackground` is left at its default (false) so polling also
  // pauses when the host switches away from the tab.
  const shouldPollTransactions = activeTab === "history" && showTxPendingNotice && !txNoticeDismissed && !txPollTimedOut;
  const txQuery = useQuery<PaymentTransactionWithBreakdown[]>({
    queryKey: ["/api/app/operations/payment-transactions"],
    refetchInterval: shouldPollTransactions ? 4000 : false,
  });

  // Task #2509 — Bound the polling window. While we're actively polling for a
  // not-yet-recorded charge, arm a ~60s timer; if it fires before the charge
  // appears we flip `txPollTimedOut`, which both stops the polling above and
  // swaps the hint for a clearer "taking longer than usual" message. The timer
  // is cleared whenever polling stops (charge arrives, host dismisses, or tab
  // change), and `txPollTimedOut` is reset wherever the notice is (re)shown.
  useEffect(() => {
    if (!shouldPollTransactions) return;
    const timer = setTimeout(() => setTxPollTimedOut(true), 60000);
    return () => clearTimeout(timer);
  }, [shouldPollTransactions]);

  // Task #2517 — Ask the server for a definitive recording status for the
  // deep-linked task whenever the "still recording" notice is up. Unlike the
  // client-side 60s timeout (which can only guess "taking longer than usual"),
  // this tells us the real outcome: the charge actually failed to record, the
  // task never needed a charge, it's still processing, or it's already recorded
  // (and the list is just stale). We keep polling it on the same cadence until
  // it reaches a terminal answer so a transient "processing" upgrades to a
  // definitive verdict on its own.
  const shouldCheckPaymentStatus =
    activeTab === "history" && showTxPendingNotice && !txNoticeDismissed && highlightTaskId != null;
  const paymentStatusQuery = useQuery<PaymentRecordingStatus>({
    queryKey: ["/api/app/operations/approvals", highlightTaskId, "payment-status"],
    enabled: shouldCheckPaymentStatus,
    refetchInterval: (query) => {
      if (!shouldCheckPaymentStatus) return false;
      const o = query.state.data?.outcome;
      // Keep polling only while the answer can still change.
      return o === "processing" || o === "recorded" || o == null ? 4000 : false;
    },
  });
  const paymentOutcome = shouldCheckPaymentStatus ? paymentStatusQuery.data?.outcome : undefined;
  const paymentReference = paymentStatusQuery.data?.reference;

  // Used to gate the Approve action: a vendor charge requires the host to
  // have at least one default card on file. Without this, the server returns
  // 412 `no_card_on_file` and the host has to start over.
  const cardsQuery = useQuery<{ id: number; isDefault: boolean; stripePaymentMethodId?: string | null; brand?: string | null; last4?: string | null; nickname?: string | null }[]>({
    queryKey: ["/api/app/operations/vendor-credit-cards"],
  });
  const hasDefaultCard = (cardsQuery.data || []).some(c => c.isDefault && c.stripePaymentMethodId);

  // Task #4074 — Manual payments already recorded drive the Payment
  // History "Paid manually" section + Edit affordance.
  const manualPaymentsQuery = useQuery<ManualPaymentRow[]>({
    queryKey: ["/api/app/operations/manual-payments"],
  });

  const approveMutation = useMutation({
    mutationFn: async ({ taskId, review, paymentRail }: { taskId: number; review: any; paymentRail?: "stripe" | "mercury_ach" }) => {
      const res = await apiRequest("POST", `/api/app/operations/approvals/${taskId}/approve`, { review, paymentRail });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      toast({ title: "Task approved" });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't approve this task. Please try again."), variant: "destructive" });
    },
  });

  // Task #4284 — Bulk close: $0 "cash" manual payment per task, moving each to
  // Paid / Closed without charging the host or emailing the vendor. For historic
  // cleanup only; lives only on this page.
  const bulkCloseMutation = useMutation({
    mutationFn: async ({ taskIds }: { taskIds: number[] }) => {
      const res = await apiRequest("POST", `/api/app/operations/approvals/bulk-close`, { taskIds });
      return res.json() as Promise<{ closed: number; skipped: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/manual-payments"] });
      setSelectedTasks([]);
      toast({
        title: "Tasks closed",
        description: `${data.closed} closed${data.skipped ? `, ${data.skipped} skipped` : ""}.`,
      });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't close tasks", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ taskId, reason }: { taskId: number; reason: string }) => {
      const res = await apiRequest("POST", `/api/app/operations/approvals/${taskId}/reject`, { reason });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      toast({ title: "Task rejected", description: "The vendor has been notified by SMS and in-app alert." });
      setRejectModalTask(null);
      setRejectReason("");
    },
    onError: (e: any) => {
      toast({ title: "Couldn't reject task", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  // Task #4074 — Edit a previously-recorded manual payment.
  const updateManualPaymentMutation = useMutation({
    mutationFn: async ({ taskId, body }: { taskId: number; body: any }) => {
      const res = await apiRequest("PATCH", `/api/app/operations/approvals/${taskId}/manual-payment`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/manual-payments"] });
      setEditingManualPayment(null);
      toast({ title: "Manual payment updated" });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't update payment", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  // Task #4084 — Void (reverse) a previously-recorded manual payment. Removes
  // the record from the paper trail and reverts the task back to "completed".
  const voidManualPaymentMutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: number }) => {
      const res = await apiRequest("DELETE", `/api/app/operations/approvals/${taskId}/manual-payment`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/manual-payments"] });
      setVoidingManualPayment(null);
      toast({ title: "Manual payment voided", description: "The vendor has been notified by email." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't void payment", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  // Parse a manual method select value (`card:<id>` or a bare off-platform
  // method) into the {method, cardId} the API expects.
  const parseManualMethod = (value: string): { method: string; cardId: number | null } | null => {
    if (!value) return null;
    if (value.startsWith("card:")) {
      const id = parseInt(value.slice(5), 10);
      return isNaN(id) ? null : { method: "card", cardId: id };
    }
    return { method: value, cardId: null };
  };

  // Dollars string → integer cents, or null when blank/invalid.
  const dollarsToCents = (value: string): number | null => {
    const n = parseFloat(value);
    if (isNaN(n) || n < 0) return null;
    return Math.round(n * 100);
  };

  // Task #4085 — Upload one or more proof-of-payment files via the mint-and-PUT
  // flow (presigned URL → direct PUT to storage), returning annotated object
  // paths the host can stage on the manual payment. Errors surface a toast.
  const uploadProofFiles = async (files: FileList | null): Promise<string[]> => {
    if (!files || files.length === 0) return [];
    const uploaded: string[] = [];
    for (const file of Array.from(files)) {
      const r = await fetch("/api/app/operations/manual-payments/upload-url", {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error("Could not start the upload. Please try again.");
      const { uploadUrl, objectPath } = await r.json();
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": uploadMimeType(file) },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed for ${file.name}`);
      uploaded.push(annotateAttachmentUrl(objectPath, file.name));
    }
    return uploaded;
  };

  const handleManualProofSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setEditManualProofUploading(true);
    try {
      const next = await uploadProofFiles(files);
      setEditManualProofUrls((prev: string[]) => [...prev, ...next]);
    } catch (e: any) {
      toast({ title: "Couldn't upload proof", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    } finally {
      setEditManualProofUploading(false);
    }
  };

  // Task #4074 — Submit an edit to an existing manual payment.
  const handleEditManualSubmit = () => {
    if (!editingManualPayment) return;
    const parsedMethod = parseManualMethod(editManualMethod);
    const amountCents = dollarsToCents(editManualAmount);
    if (!parsedMethod || amountCents == null) return;
    updateManualPaymentMutation.mutate({
      taskId: editingManualPayment.taskId,
      body: {
        amountCents,
        method: parsedMethod.method,
        cardId: parsedMethod.cardId,
        note: editManualNote || null,
        proofUrls: editManualProofUrls,
      },
    });
  };

  const approvals = approvalsQuery.data || [];

  // Task #2469 — Once the pending approvals have loaded, scroll the deep-linked
  // task into view and pulse a highlight ring. Switch to the approvals tab if a
  // stale `?tab=` happened to leave us elsewhere.
  useEffect(() => {
    if (highlightTaskId == null) return;
    if (!approvals.some((t) => t.id === highlightTaskId)) return;
    if (activeTab !== "approvals") setActiveTab("approvals");
    const el = approvalCardRefs.current.get(highlightTaskId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setActiveHighlight(highlightTaskId);
    const timer = setTimeout(() => setActiveHighlight(null), 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTaskId, approvalsQuery.data]);

  // Task #2475 — On the Payment History tab, scroll to and pulse the transaction
  // row whose source task matches the deep-linked `?task=N`. Falls back to just
  // opening the tab when no transaction exists yet for that task.
  useEffect(() => {
    if (highlightTaskId == null) return;
    if (activeTab !== "history") return;
    if (txQuery.isLoading) return;
    const tx = (txQuery.data || []).find((t) => t.taskId === highlightTaskId);
    if (!tx) {
      // Task #2482 — No transaction yet for the deep-linked task. Surface a
      // brief inline hint so the host understands why nothing is highlighted.
      setShowTxPendingNotice(true);
      return;
    }
    // The charge has been recorded — drop the "still recording" hint (and any
    // timed-out state) and let the existing scroll/highlight take over.
    setShowTxPendingNotice(false);
    setTxPollTimedOut(false);
    const el = txRowRefs.current.get(tx.id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setActiveTxHighlight(tx.id);
    const timer = setTimeout(() => setActiveTxHighlight(null), 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTaskId, activeTab, txQuery.data, txQuery.isLoading]);

  const selectedTasksSet = useMemo(() => new Set(selectedTasks), [selectedTasks]);
  const { handleItemClick: handleTaskClick } = useRangeSelection(
    approvals.map((t) => t.id),
    selectedTasksSet,
    (next) => setSelectedTasks(Array.from(next)),
  );
  const vendors = vendorsQuery.data || [];
  const properties = propertiesQuery.data || [];
  const transactions = txQuery.data || [];
  const vendorMap = new Map(vendors.map((v) => [v.id, v]));
  const propertyMap = new Map(properties.map((p) => [p.id, p]));

  const totalPending = approvals.length;
  const totalAmount = approvals.reduce((sum, t) => sum + (t.priceCents || 0), 0);

  const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const filteredTx = transactions.filter(tx => {
    if (statusFilter && statusFilter !== "all" && tx.status !== statusFilter) return false;
    if (vendorFilter && vendorFilter !== "all" && String(tx.vendorId) !== vendorFilter) return false;
    return true;
  });

  const totalPaid = transactions.filter(t => t.status === "succeeded").reduce((s, t) => s + (t.hostTotalCents || 0), 0);
  const totalPendingTx = transactions.filter(t => t.status === "pending" || t.status === "processing").reduce((s, t) => s + (t.hostTotalCents || 0), 0);
  const totalFees = transactions.filter(t => t.status === "succeeded").reduce((s, t) => s + (t.stripeFeeCents || 0), 0);
  const txCount = transactions.length;

  const handleExportCSV = () => {
    const headers = ["Date", "Vendor", "Property", "Vendor Amount", "Fee", "Host Total", "Status"];
    const rows = filteredTx.map(tx => [
      tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "",
      vendorMap.get(tx.vendorId)?.name || `Vendor #${tx.vendorId}`,
      tx.propertyId ? (propertyMap.get(tx.propertyId)?.name || `Property #${tx.propertyId}`) : "",
      formatCentsShared(tx.vendorAmountCents),
      formatCentsShared(tx.stripeFeeCents),
      formatCentsShared(tx.hostTotalCents),
      tx.status || "pending",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="page-payment-approvals">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-approvals-title" data-tour="vendor-payments-heading">Vendor Payments</h1>
            <p className="text-sm text-gray-500 mt-1">
              {totalPending} pending · {formatCents(totalAmount)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="vendor-payments" />
            <GuideButton pageKey="vendor-payments" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="vendor-payments" />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="approvals" data-testid="tab-pending-approvals">Pending Approvals</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-payment-history">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="approvals">
            {approvals.length > 0 && (
              <div className="flex items-center justify-between gap-3 mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="text-approvals-summary">
                  {totalPending} pending approval{totalPending === 1 ? "" : "s"}
                </p>
                <Button
                  variant="outline"
                  disabled={readOnly || bulkCloseMutation.isPending}
                  title="Close every remaining pending approval with no host charge ($0). For historic cleanup — moves them all to Paid / Closed."
                  onClick={() => {
                    const ids = approvals.map((t) => t.id);
                    if (ids.length === 0) return;
                    if (!window.confirm(`Close all ${ids.length} remaining task${ids.length === 1 ? "" : "s"} with no host charge ($0)? This moves them to Paid / Closed.`)) return;
                    bulkCloseMutation.mutate({ taskIds: ids });
                  }}
                  data-testid="button-bulk-close-all"
                >
                  {bulkCloseMutation.isPending ? "Closing…" : `Archive / close all remaining ($0) (${totalPending})`}
                </Button>
              </div>
            )}
            {selectedTasks.length > 0 && (
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300 flex-1 mr-3">
                  Bulk approve assigns 5 stars with no comment. Use single approve to leave detailed feedback.
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    disabled={readOnly || bulkCloseMutation.isPending}
                    title="Close the selected tasks with no host charge ($0). For historic cleanup — moves them straight to Paid / Closed."
                    onClick={() => {
                      if (selectedTasks.length === 0) return;
                      if (!window.confirm(`Close ${selectedTasks.length} task${selectedTasks.length === 1 ? "" : "s"} with no host charge ($0)? This moves them to Paid / Closed.`)) return;
                      bulkCloseMutation.mutate({ taskIds: selectedTasks });
                    }}
                    data-testid="button-bulk-close"
                  >
                    {bulkCloseMutation.isPending ? "Closing…" : `Close Selected ($0) (${selectedTasks.length})`}
                  </Button>
                  <Button
                    disabled={readOnly || !hasDefaultCard}
                    title={!hasDefaultCard ? "Bulk approve uses your card on file. Add one in Vendor Settings, or approve individually to use Mercury ACH." : undefined}
                    onClick={() => {
                      selectedTasks.forEach((taskId) => {
                        approveMutation.mutate({
                          taskId,
                          review: { overallRating: 5 },
                        });
                      });
                      setSelectedTasks([]);
                    }}
                    data-testid="button-bulk-approve"
                    data-tour="vendor-payments-approve"
                  >
                    Approve Selected ({selectedTasks.length})
                  </Button>
                </div>
              </div>
            )}

            {approvalsQuery.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : approvals.length === 0 ? (
              <div className="text-center py-16 text-gray-500" data-testid="text-no-approvals">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-300" />
                <p className="text-lg font-medium text-gray-700">All caught up</p>
                <p className="text-sm mt-1">No pending approvals right now.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {approvals.map((task) => {
                  const vendor = task.vendorId ? vendorMap.get(task.vendorId) : null;
                  const property = task.propertyId ? propertyMap.get(task.propertyId) : null;
                  const vendorAmount = task.priceCents || 0;
                  const breakdown = computeVendorFeeBreakdown(vendorAmount);
                  const fee = breakdown.padpulseFeeCents;
                  const total = breakdown.hostTotalCents;

                  // Task #3826 — Resolve the owner of this task's property (via
                  // its group name) and whether the vendor cost exceeds the
                  // owner's pre-approved threshold. If so, the PM can request
                  // explicit owner sign-off; once raised, show the status here.
                  const taskOwner = property?.propertyGroup
                    ? ownerByGroupName.get(property.propertyGroup)
                    : undefined;
                  const existingApproval = approvalByTaskId.get(task.id);
                  const aboveThreshold =
                    !!taskOwner && vendorAmount > (taskOwner.preApprovedThresholdCents || 0);

                  return (
                    <div
                      key={task.id}
                      ref={(el) => {
                        if (el) approvalCardRefs.current.set(task.id, el);
                        else approvalCardRefs.current.delete(task.id);
                      }}
                      className={`border rounded-lg p-4 transition-shadow ${activeHighlight === task.id ? "ring-2 ring-purple-400 ring-offset-2 shadow-md" : ""}`}
                      data-testid={`card-approval-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div onClick={(e) => { e.stopPropagation(); handleTaskClick(task.id, e.shiftKey); }}>
                          <Checkbox
                            checked={selectedTasks.includes(task.id)}
                            onCheckedChange={() => {}}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-gray-900">{task.title}</h3>
                              <p className="text-sm text-gray-500">
                                {property?.name || "No property"} · {vendor?.name || "Unassigned"} · Assigned to: {task.assignedTeamMemberName || "Owner"}
                              </p>
                            </div>
                          </div>

                          <div className="mt-2">
                            <PriceBreakdownLines
                              task={task}
                              testId={`section-price-breakdown-${task.id}`}
                            />
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="text-gray-400">Vendor receives:</span>{" "}
                              <span className="font-medium" data-testid={`text-vendor-payout-${task.id}`}>{formatCents(breakdown.vendorPayoutCents)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">PadPulse fee:</span>{" "}
                              <span className="font-medium" data-testid={`text-padpulse-fee-${task.id}`}>{formatCents(fee)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Total charge:</span>{" "}
                              <span className="font-semibold text-gray-900" data-testid={`text-host-total-${task.id}`}>{formatCents(total)}</span>
                            </div>
                          </div>

                          {/* Task #3826 — Owner pre-approval affordance/status */}
                          {existingApproval ? (
                            <div className="mt-3" data-testid={`section-owner-approval-${task.id}`}>
                              <Badge
                                className={
                                  existingApproval.status === "approved"
                                    ? "bg-green-100 text-green-800"
                                    : existingApproval.status === "rejected"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                }
                                data-testid={`badge-owner-approval-${task.id}`}
                              >
                                {existingApproval.status === "approved"
                                  ? `Approved by ${existingApproval.respondedByOwnerName ?? "owner"}`
                                  : existingApproval.status === "rejected"
                                    ? `Rejected by ${existingApproval.respondedByOwnerName ?? "owner"}`
                                    : "Owner approval pending"}
                              </Badge>
                              {existingApproval.status === "rejected" && existingApproval.ownerNote && (
                                <p className="text-xs text-gray-500 mt-1" data-testid={`text-owner-approval-note-${task.id}`}>
                                  Owner note: {existingApproval.ownerNote}
                                </p>
                              )}
                            </div>
                          ) : aboveThreshold && taskOwner ? (
                            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`section-owner-approval-${task.id}`}>
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                This {formatCents(vendorAmount)} cost is above {taskOwner.name}'s pre-approved limit of {formatCents(taskOwner.preApprovedThresholdCents || 0)}.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={readOnly || requestApprovalMutation.isPending}
                                onClick={() =>
                                  requestApprovalMutation.mutate({
                                    taskId: task.id,
                                    ownerId: taskOwner.id,
                                    taskTitle: task.title,
                                    taskCostCents: vendorAmount,
                                    propertyAddress: property?.address ?? property?.name ?? null,
                                    vendorName: vendor?.name ?? null,
                                  })
                                }
                                data-testid={`button-request-owner-approval-${task.id}`}
                              >
                                Request owner approval
                              </Button>
                            </div>
                          ) : null}

                          <div className="flex items-center gap-4 mt-4">
                            <Button
                              size="sm"
                              className="bg-brand hover:bg-brand-hover text-white"
                              disabled={readOnly || !task.vendorId}
                              title={!task.vendorId ? "Assign a vendor to this task before approving." : readOnly ? "You don't have permission to approve tasks." : "Approve and pay the vendor for this task"}
                              onClick={() => setApproveModalTask(task)}
                              data-testid={`button-approve-pay-${task.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve &amp; Pay
                            </Button>
                            <Link href={`/app/tasks?task=${task.id}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!task.vendorId}
                                title={!task.vendorId ? "Assign a vendor to this task before opening the chat." : "Open the task to chat with the vendor"}
                                data-testid={`button-open-chat-${task.id}`}
                              >
                                <MessageCircle className="h-4 w-4 mr-1" />
                                Chat
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setRejectModalTask(task); setRejectReason(""); }}
                              disabled={readOnly || rejectMutation.isPending}
                              data-testid={`button-reject-${task.id}`}
                            >
                              Reject
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-auto text-gray-500 hover:text-gray-700"
                              onClick={() => setArchiveModalTask(task)}
                              disabled={readOnly}
                              title="Close this task with no host charge ($0) — for historic cleanup."
                              data-testid={`button-archive-${task.id}`}
                            >
                              <Archive className="h-4 w-4 mr-1" />
                              Archive
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-6">
              <div className="flex items-center justify-end">
                <Button variant="outline" onClick={handleExportCSV} data-testid="button-export-csv">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                      <DollarSign className="h-4 w-4" />
                      Total Paid
                    </div>
                    <p className="text-xl font-semibold text-gray-900" data-testid="text-total-paid">{formatCentsShared(totalPaid)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                      <Clock className="h-4 w-4" />
                      Pending
                    </div>
                    <p className="text-xl font-semibold text-yellow-600" data-testid="text-total-pending">{formatCentsShared(totalPendingTx)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                      <CreditCard className="h-4 w-4" />
                      PadPulse Fees
                    </div>
                    <p className="text-xl font-semibold text-gray-900" data-testid="text-total-fees">{formatCentsShared(totalFees)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                      <TrendingUp className="h-4 w-4" />
                      Transactions
                    </div>
                    <p className="text-xl font-semibold text-gray-900" data-testid="text-tx-count">{txCount}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="select-status-filter" data-tour="vendor-payments-list">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="succeeded">Succeeded</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="requires_3ds">Requires 3DS</SelectItem>
                    <SelectItem value="awaiting_mercury_approval">Awaiting Mercury Approval</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="select-vendor-filter" data-tour="vendor-payments-filters">
                    <SelectValue placeholder="Vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Task #2482 / #2517 — Notice shown when a host deep-links from
                  "View Payment Status" but no matching charge row is in the list
                  yet. Task #2517 makes this definitive: instead of only guessing
                  "taking longer than usual" after a client-side timeout, the
                  server tells us the real outcome so we can show "failed to
                  record" (with a retry/support reference) or "no charge needed"
                  vs. a genuine "still processing" state. */}
              {showTxPendingNotice && !txNoticeDismissed && (() => {
                // Pick the notice variant from the server's definitive outcome,
                // falling back to the legacy timeout-driven states while the
                // status check is still loading. The variant logic lives in a
                // pure, unit-tested module (see lib/payment-notice.ts).
                const notice = resolvePaymentNotice(paymentOutcome, txPollTimedOut);
                const kind = notice.kind;
                const styles: Record<typeof kind, { box: string; icon: string; dismiss: string }> = {
                  failed: {
                    box: "border-red-200 bg-red-50 text-red-800",
                    icon: "text-red-500",
                    dismiss: "text-red-400 hover:text-red-600",
                  },
                  "no_charge": {
                    box: "border-gray-200 bg-gray-50 text-gray-700",
                    icon: "text-gray-400",
                    dismiss: "text-gray-400 hover:text-gray-600",
                  },
                  "timed-out": {
                    box: "border-amber-200 bg-amber-50 text-amber-800",
                    icon: "text-amber-500",
                    dismiss: "text-amber-400 hover:text-amber-600",
                  },
                  pending: {
                    box: "border-blue-200 bg-blue-50 text-blue-800",
                    icon: "text-blue-500",
                    dismiss: "text-blue-400 hover:text-blue-600",
                  },
                };
                const s = styles[kind];
                return (
                  <div
                    className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${s.box}`}
                    data-testid="notice-payment-pending"
                    data-state={kind}
                    role="status"
                  >
                    {kind === "failed"
                      ? <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${s.icon}`} />
                      : <Info className={`h-4 w-4 mt-0.5 shrink-0 ${s.icon}`} />}
                    <div className="flex-1 space-y-2">
                      <p>{notice.message}</p>
                      {notice.showRetry && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 border-red-300 bg-white text-red-700 hover:bg-red-100"
                            onClick={() => {
                              setTxPollTimedOut(false);
                              paymentStatusQuery.refetch();
                              txQuery.refetch();
                            }}
                            data-testid="button-retry-payment-status"
                          >
                            Retry
                          </Button>
                          {paymentReference && (
                            <span className="text-xs text-red-700" data-testid="text-payment-reference">
                              Contact support with reference{" "}
                              <span className="font-mono font-medium">{paymentReference}</span>.
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setTxNoticeDismissed(true)}
                      className={`shrink-0 ${s.dismiss}`}
                      aria-label="Dismiss notice"
                      data-testid="button-dismiss-payment-notice"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })()}

              {txQuery.isLoading && (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              )}

              {filteredTx.length === 0 && !txQuery.isLoading && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No transactions yet</p>
                    <p className="text-sm text-gray-400 mt-1">Payment transactions will appear here after approving tasks</p>
                  </CardContent>
                </Card>
              )}

              {filteredTx.length > 0 && (
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table className="min-w-[720px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Property</TableHead>
                          <TableHead className="text-right">Vendor Amt</TableHead>
                          <TableHead className="text-right">PadPulse Fee</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTx.map(tx => {
                          const txVendor = vendorMap.get(tx.vendorId);
                          const txProperty = tx.propertyId ? propertyMap.get(tx.propertyId) : null;
                          return (
                            <TableRow
                              key={tx.id}
                              data-testid={`row-transaction-${tx.id}`}
                              ref={(el) => {
                                if (el) txRowRefs.current.set(tx.id, el);
                                else txRowRefs.current.delete(tx.id);
                              }}
                              className={activeTxHighlight === tx.id ? "ring-2 ring-purple-400 ring-inset bg-purple-50 transition-colors" : "transition-colors"}
                            >
                              <TableCell className="text-sm text-gray-600">
                                {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "-"}
                              </TableCell>
                              <TableCell className="font-medium">
                                {txVendor?.name || `Vendor #${tx.vendorId}`}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {txProperty?.name || "-"}
                              </TableCell>
                              <TableCell className="text-right text-sm" data-testid={`text-tx-vendor-amount-${tx.id}`}>{formatCentsShared(tx.vendorAmountCents)}</TableCell>
                              <TableCell className="text-right text-sm text-gray-500" data-testid={`text-tx-padpulse-fee-${tx.id}`}>{formatCentsShared(tx.stripeFeeCents)}</TableCell>
                              <TableCell className="text-right font-medium" data-testid={`text-tx-total-${tx.id}`}>{formatCentsShared(tx.hostTotalCents)}</TableCell>
                              <TableCell>
                                <Badge className={STATUS_COLORS[tx.status || "pending"] || "bg-gray-100 text-gray-800"} data-testid={`badge-tx-status-${tx.id}`}>
                                  {tx.status || "pending"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Task #4074 — Off-platform (manual) payments recorded by the host. */}
              {(manualPaymentsQuery.data || []).length > 0 && (
                <div className="space-y-2" data-testid="section-manual-payments">
                  <h3 className="text-sm font-semibold text-gray-700">Manual payments</h3>
                  <Card>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table className="min-w-[640px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Vendor</TableHead>
                            <TableHead>Task</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Proof</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(manualPaymentsQuery.data || []).map((mp) => {
                            const cardLabel = mp.cardId
                              ? (() => {
                                  const c = (cardsQuery.data || []).find((x) => x.id === mp.cardId);
                                  return c ? `Card •••• ${c.last4 ?? "****"}` : "Card";
                                })()
                              : MANUAL_METHOD_LABELS[mp.method] || mp.method;
                            return (
                              <TableRow key={mp.id} data-testid={`row-manual-payment-${mp.id}`}>
                                <TableCell className="text-sm text-gray-600">
                                  {mp.createdAt ? new Date(mp.createdAt).toLocaleDateString() : "-"}
                                </TableCell>
                                <TableCell className="font-medium">{mp.vendorName || (mp.vendorId ? `Vendor #${mp.vendorId}` : "-")}</TableCell>
                                <TableCell className="text-sm text-gray-600">{mp.taskTitle || `Task #${mp.taskId}`}</TableCell>
                                <TableCell className="text-sm text-gray-600" data-testid={`text-manual-method-${mp.id}`}>{cardLabel}</TableCell>
                                <TableCell className="text-right font-medium" data-testid={`text-manual-amount-${mp.id}`}>{formatCentsShared(mp.amountCents)}</TableCell>
                                <TableCell>
                                  {(mp.proofUrls && mp.proofUrls.length > 0) ? (
                                    <AttachmentPreviews urls={mp.proofUrls} idPrefix={mp.id} />
                                  ) : (
                                    <span className="text-sm text-gray-400" data-testid={`text-manual-proof-empty-${mp.id}`}>—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={readOnly}
                                    onClick={() => {
                                      setEditingManualPayment(mp);
                                      setEditManualAmount((mp.amountCents / 100).toFixed(2));
                                      setEditManualMethod(mp.cardId ? `card:${mp.cardId}` : mp.method);
                                      setEditManualNote(mp.note || "");
                                      setEditManualProofUrls(mp.proofUrls || []);
                                      setEditManualProofUploading(false);
                                    }}
                                    data-testid={`button-edit-manual-payment-${mp.id}`}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                    disabled={readOnly}
                                    onClick={() => setVoidingManualPayment(mp)}
                                    data-testid={`button-void-manual-payment-${mp.id}`}
                                  >
                                    Void
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Task #2196 — Reason-required reject dialog */}
        <Dialog open={!!rejectModalTask} onOpenChange={(open) => { if (!open && rejectMutation.isPending) return; if (!open) { setRejectModalTask(null); setRejectReason(""); } }}>
          <DialogContent
            className="max-w-md"
            data-testid="dialog-reject-task"
            onInteractOutside={(e) => { if (rejectMutation.isPending) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (rejectMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (rejectMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Reject Task</DialogTitle>
            </DialogHeader>
            {rejectModalTask && (
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-gray-900">{rejectModalTask.title}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    The vendor will be notified by SMS and an in-app red alert.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Reason for rejection <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explain what was wrong so the vendor knows what to fix…"
                    rows={4}
                    className="mt-1"
                    data-testid="textarea-reject-reason"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setRejectModalTask(null); setRejectReason(""); }}
                    data-testid="button-reject-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!rejectReason.trim() || rejectMutation.isPending}
                    onClick={() => rejectMutation.mutate({ taskId: rejectModalTask.id, reason: rejectReason.trim() })}
                    data-testid="button-reject-confirm"
                  >
                    {rejectMutation.isPending ? "Rejecting…" : "Reject task"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <ApproveAndPayModal
          task={approveModalTask}
          open={!!approveModalTask}
          onClose={() => setApproveModalTask(null)}
        />

        <ApproveAndPayModal
          task={archiveModalTask}
          open={!!archiveModalTask}
          onClose={() => setArchiveModalTask(null)}
          mode="archive"
        />

        {/* Task #4074 — Edit an existing manual payment. */}
        <Dialog
          open={!!editingManualPayment}
          onOpenChange={(open) => {
            if (!open && updateManualPaymentMutation.isPending) return;
            if (!open) setEditingManualPayment(null);
          }}
        >
          <DialogContent
            className="max-w-md max-h-[90vh] overflow-y-auto"
            data-testid="dialog-edit-manual-payment"
            onInteractOutside={(e) => { if (updateManualPaymentMutation.isPending) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (updateManualPaymentMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (updateManualPaymentMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Edit Manual Payment</DialogTitle>
            </DialogHeader>
            {editingManualPayment && (
              <div className="space-y-4">
                <div>
                  <p className="font-medium">{editingManualPayment.taskTitle || `Task #${editingManualPayment.taskId}`}</p>
                  <p className="text-sm text-gray-500">{editingManualPayment.vendorName || ""}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Amount paid</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editManualAmount}
                      onChange={(e) => setEditManualAmount(e.target.value)}
                      className="w-full rounded-md border border-gray-300 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      data-testid="input-edit-manual-amount"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Payment method</label>
                  <Select value={editManualMethod} onValueChange={setEditManualMethod}>
                    <SelectTrigger className="w-full" data-testid="select-edit-manual-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {(cardsQuery.data || []).map((c) => (
                        <SelectItem key={c.id} value={`card:${c.id}`}>
                          {c.nickname || c.brand || "Card"} •••• {c.last4 ?? "****"}{c.isDefault ? " (default)" : ""}
                        </SelectItem>
                      ))}
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="venmo">Venmo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Note (optional)</label>
                  <Textarea
                    value={editManualNote}
                    onChange={(e) => setEditManualNote(e.target.value)}
                    rows={2}
                    data-testid="textarea-edit-manual-note"
                  />
                </div>

                <ManualProofField
                  urls={editManualProofUrls}
                  uploading={editManualProofUploading}
                  readOnly={readOnly}
                  onSelect={(files) => handleManualProofSelect(files)}
                  onRemove={(idx) => setEditManualProofUrls((prev) => prev.filter((_, i) => i !== idx))}
                  idPrefix="edit-manual"
                />

                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => setEditingManualPayment(null)}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-brand hover:bg-brand-hover"
                    onClick={handleEditManualSubmit}
                    disabled={readOnly || !editManualMethod || dollarsToCents(editManualAmount) == null || updateManualPaymentMutation.isPending}
                    data-testid="button-save-manual-payment"
                  >
                    {updateManualPaymentMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Task #4084 — Confirm voiding (reversing) an existing manual payment. */}
        <Dialog
          open={!!voidingManualPayment}
          onOpenChange={(open) => {
            if (!open && voidManualPaymentMutation.isPending) return;
            if (!open) setVoidingManualPayment(null);
          }}
        >
          <DialogContent
            className="max-w-md"
            data-testid="dialog-void-manual-payment"
            onInteractOutside={(e) => { if (voidManualPaymentMutation.isPending) e.preventDefault(); }}
            onEscapeKeyDown={(e) => { if (voidManualPaymentMutation.isPending) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (voidManualPaymentMutation.isPending) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle>Void Manual Payment</DialogTitle>
            </DialogHeader>
            {voidingManualPayment && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  This removes the recorded payment of{" "}
                  <span className="font-semibold">{formatCentsShared(voidingManualPayment.amountCents)}</span>
                  {voidingManualPayment.vendorName ? <> to <span className="font-semibold">{voidingManualPayment.vendorName}</span></> : null}
                  {" "}for{" "}
                  <span className="font-semibold">{voidingManualPayment.taskTitle || `Task #${voidingManualPayment.taskId}`}</span>.
                  The task returns to awaiting payment, an audit entry is logged, and the vendor is notified by email. This can't be undone.
                </p>
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setVoidingManualPayment(null)}
                    disabled={voidManualPaymentMutation.isPending}
                    data-testid="button-cancel-void-manual-payment"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => voidManualPaymentMutation.mutate({ taskId: voidingManualPayment.taskId })}
                    disabled={readOnly || voidManualPaymentMutation.isPending}
                    data-testid="button-confirm-void-manual-payment"
                  >
                    {voidManualPaymentMutation.isPending ? "Voiding..." : "Void Payment"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </AppLayout>
  );
}
